import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react"

import { cn } from "@verlo/shared/lib/utils"

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/80 flex items-center justify-center data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props} />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

/**
 * Three dialog sizes, by what's in them — not a `max-w-*` picked per dialog:
 *   confirm  one question, one or two buttons
 *   form     a form a server fills at the till (the default)
 *   sheet    a wide working surface (a menu item's modifiers, a plan)
 * A dialog never scrolls sideways, and never grows past the screen: it is a column with the
 * header and footer pinned and a `DialogBody` scrolling between them (a dialog with no
 * `DialogBody` scrolls as a whole, header included, which is the old behaviour and fine for
 * anything short). The close button is a 40px tap target on the FOH (App.css).
 */
const SIZES = { confirm: "max-w-sm", form: "max-w-lg", sheet: "max-w-2xl" }

const DialogContent = React.forwardRef(({ className, children, size = "form", ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay>
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          "relative z-50 flex w-full flex-col gap-4 border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 sm:rounded-lg max-h-[90vh] overflow-y-auto overflow-x-hidden",
          SIZES[size] || SIZES.form,
          className
        )}
        {...props}>
        {children}
        <DialogPrimitive.Close
          className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground"
          data-testid="dialog-close">
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </DialogPrimitive.Close>
      </DialogPrimitive.Content>
    </DialogOverlay>
  </DialogPortal>
))
DialogContent.displayName = DialogPrimitive.Content.displayName

/** The part of a dialog that scrolls, between a pinned header and footer. */
const DialogBody = ({ className, ...props }) => (
  <div className={cn("min-h-0 flex-1 overflow-y-auto overflow-x-hidden", className)} {...props} />
)
DialogBody.displayName = "DialogBody"

const DialogHeader = ({
  className,
  ...props
}) => (
  <div
    className={cn("flex shrink-0 flex-col space-y-1.5 text-center sm:text-left", className)}
    {...props} />
)
DialogHeader.displayName = "DialogHeader"

const DialogFooter = ({
  className,
  ...props
}) => (
  <div
    // `gap`, not `space-x`: a footer that stacks its buttons (`sm:flex-col`) kept the margin
    // space-x puts on every button but the first, and its second button sat 8px in.
    className={cn("flex shrink-0 flex-col-reverse gap-2 sm:flex-row sm:justify-end", className)}
    {...props} />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("text-lg font-semibold leading-none tracking-tight", className)}
    {...props} />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props} />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogBody,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
